import { PERFORMANCE_LENS_ADMIN, PERFORMANCE_LENS_GIVE_FEEDBACK, PERFORMANCE_LENS_HOD, PERFORMANCE_LENS_MY_FEEDBACK, PERFORMANCE_LENS_PM, PERFORMANCE_LENS_REVIEWS, PERFORMANCE_LENS_SETS, PERFORMANCE_LENS_TRANSPARENCY } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import { getApi, postApi, putApi } from "../../utils/api.ts";
import type { AdminInsights, AssignmentPreview, CycleConfig, CycleOverview, DesignationRoleMapping, GiveFeedbackData, HodOverview, LensReview, PmSubmissionsData, PublishedFeedback, QuestionConfig, TransparencyCycle, TransparencyProject, TransparencyProjectDetail } from "./types.ts";

const token = () => getAuthToken("accessToken");

export async function getTransparencyCycles(signal?: AbortSignal): Promise<TransparencyCycle[]> {
  const response = await getApi<TransparencyCycle[]>(`${PERFORMANCE_LENS_TRANSPARENCY}/cycles`, token(), "json", signal);
  return response.data;
}

export async function getTransparencyProjects(cycleNumber: number, signal?: AbortSignal): Promise<TransparencyProject[]> {
  const response = await getApi<TransparencyProject[]>(`${PERFORMANCE_LENS_TRANSPARENCY}/cycles/${encodeURIComponent(cycleNumber)}/projects`, token(), "json", signal);
  return response.data;
}

export async function getTransparencyProject(cycleNumber: number, projectId: number, signal?: AbortSignal): Promise<TransparencyProjectDetail> {
  const response = await getApi<TransparencyProjectDetail>(`${PERFORMANCE_LENS_TRANSPARENCY}/cycles/${encodeURIComponent(cycleNumber)}/projects/${encodeURIComponent(projectId)}`, token(), "json", signal);
  return response.data;
}

export async function getGiveFeedback(signal?: AbortSignal): Promise<GiveFeedbackData> {
  const response = await getApi<GiveFeedbackData>(PERFORMANCE_LENS_GIVE_FEEDBACK, token(), "json", signal);
  return response.data;
}

export async function getMyFeedback(signal?: AbortSignal): Promise<PublishedFeedback[]> {
  const response = await getApi<PublishedFeedback[]>(PERFORMANCE_LENS_MY_FEEDBACK, token(), "json", signal);
  return response.data;
}

export async function getAdminCycles(signal?: AbortSignal): Promise<CycleOverview[]> {
  const response = await getApi<CycleOverview[]>(`${PERFORMANCE_LENS_ADMIN}/overview`, token(), "json", signal);
  return response.data;
}

export async function getAdminInsights(cycleNumber: number, signal?: AbortSignal): Promise<AdminInsights> {
  const response = await getApi<AdminInsights>(`${PERFORMANCE_LENS_ADMIN}/cycles/${encodeURIComponent(cycleNumber)}/insights`, token(), "json", signal);
  return response.data;
}

export async function exportAdminRatings(cycleNumber: number): Promise<Blob> {
  const response = await fetch(`${PERFORMANCE_LENS_ADMIN}/cycles/${encodeURIComponent(cycleNumber)}/ratings-export`, {
    method: "POST",
    headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
  });
  if (!response.ok) throw new Error(`Could not export ratings (HTTP ${response.status}).`);
  return response.blob();
}

export async function getPmSubmissions(signal?: AbortSignal): Promise<PmSubmissionsData> {
  const response = await getApi<PmSubmissionsData>(`${PERFORMANCE_LENS_PM}/submissions`, token(), "json", signal);
  return response.data;
}

export async function getHodOverview(signal?: AbortSignal): Promise<HodOverview> {
  const response = await getApi<HodOverview>(`${PERFORMANCE_LENS_HOD}/overview`, token(), "json", signal);
  return response.data;
}

export async function getCycleConfiguration(signal?: AbortSignal): Promise<CycleConfig[]> {
  const response = await getApi<CycleConfig[]>(`${PERFORMANCE_LENS_ADMIN}/config/cycles`, token(), "json", signal);
  return response.data;
}

export async function getQuestionConfiguration(cycleNumber: number, signal?: AbortSignal): Promise<QuestionConfig[]> {
  const response = await getApi<QuestionConfig[]>(`${PERFORMANCE_LENS_ADMIN}/config/questions?cycleNumber=${encodeURIComponent(cycleNumber)}`, token(), "json", signal);
  return response.data;
}

export async function versionQuestion(value: Omit<QuestionConfig, "id" | "effectiveToCycle" | "active">): Promise<QuestionConfig> {
  const response = await postApi<QuestionConfig>(`${PERFORMANCE_LENS_ADMIN}/config/questions/versions`, token(), value);
  return response.data;
}

export async function retireQuestion(questionKey: string, effectiveFromCycle: number): Promise<void> {
  await postApi(`${PERFORMANCE_LENS_ADMIN}/config/questions/${encodeURIComponent(questionKey)}/retire?effectiveFromCycle=${encodeURIComponent(effectiveFromCycle)}`, token(), {});
}

export async function getDesignationRoleMappings(signal?: AbortSignal): Promise<DesignationRoleMapping[]> {
  const response = await getApi<DesignationRoleMapping[]>(`${PERFORMANCE_LENS_ADMIN}/config/designation-roles`, token(), "json", signal);
  return response.data;
}

export async function syncDesignationRoleMappings(): Promise<{ discovered: number; added: number }> {
  const response = await postApi<{ discovered: number; added: number }>(`${PERFORMANCE_LENS_ADMIN}/config/designation-roles/sync`, token(), {});
  return response.data;
}

export async function updateDesignationRoleMapping(mapping: DesignationRoleMapping, selection: string): Promise<DesignationRoleMapping> {
  const mappingStatus = selection === "UNMAPPED" || selection === "EXCLUDED" ? selection : "MAPPED";
  const response = await putApi<DesignationRoleMapping>(
    `${PERFORMANCE_LENS_ADMIN}/config/designation-roles/${encodeURIComponent(mapping.designationCode)}`,
    token(), { mappingStatus, roleCode: mappingStatus === "MAPPED" ? selection : null, rowVersion: mapping.rowVersion },
  );
  return response.data;
}

export async function previewAssignments(cycleNumber: number): Promise<AssignmentPreview> {
  const response = await getApi<AssignmentPreview>(`${PERFORMANCE_LENS_ADMIN}/cycles/${cycleNumber}/assignment-preview`, token());
  return response.data;
}

export async function publishCycle(cycleNumber: number): Promise<{ cycleNumber: number; employeeSnapshots: number }> {
  const response = await postApi<{ cycleNumber: number; employeeSnapshots: number }>(`${PERFORMANCE_LENS_ADMIN}/cycles/${cycleNumber}/publish`, token(), {});
  return response.data;
}

export async function saveReviewDraft(review: LensReview): Promise<LensReview> {
  const response = await putApi<LensReview>(`${PERFORMANCE_LENS_REVIEWS}/${review.id}/draft`, token(), {
    notEnoughInteraction: review.notEnoughInteraction,
    rowVersion: review.rowVersion,
    comment: review.comment,
    selfNote: review.selfNote ?? "",
    ratings: review.questions.filter(question => question.rating !== null)
      .map(question => ({ questionId: question.id, value: question.rating })),
  });
  return response.data;
}

export async function submitReviewSet(cycleId: number, projectId: number): Promise<void> {
  await postApi(`${PERFORMANCE_LENS_SETS}/submit`, token(), { cycleId, projectId });
}

export async function submitAllReadySets(sets: { cycleId: number; projectId: number }[]): Promise<void> {
  await postApi(`${PERFORMANCE_LENS_SETS}/submit-ready`, token(), { sets });
}
